import { Button, Grid, Space } from "antd";
import { useCallback, useRef, useState } from "react";
import { hashString } from "@/common/utils";
import {
	DocumentEditor,
	type DocumentViewMode,
	DocumentViewSelector,
} from "./document-editor";
import { CustomDrawer } from "./layout/workspace/custom-drawer";

interface DocumentEditorDrawerProps {
	title: string;
	content: string;
	open: boolean;
	onClose: () => void;
	onSave?: (content: string) => void;
	readOnly?: boolean;
}

/** A full-screen drawer for viewing and editing a markdown document. */
export function DocumentEditorDrawer({
	title,
	content: initialContent,
	open,
	onClose,
	onSave,
	readOnly = false,
}: DocumentEditorDrawerProps) {
	const [viewMode, setViewMode] = useState<DocumentViewMode>("preview");
	const [content, setContent] = useState(initialContent);
	const savedHashRef = useRef(hashString(initialContent));
	const [isDirty, setIsDirty] = useState(false);
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;

	const handleChange = useCallback((newContent: string) => {
		setIsDirty(hashString(newContent) !== savedHashRef.current);
		setContent(newContent);
	}, []);

	return (
		<CustomDrawer
			title={title}
			open={open}
			onClose={onClose}
			size="large"
			styles={{ wrapper: { minHeight: isMobile ? "90%" : undefined } }}
			extra={
				<Space>
					{!readOnly && (
						<Button
							type="primary"
							onClick={() => onSave?.(content)}
							disabled={!isDirty}
						>
							Save
						</Button>
					)}
					<DocumentViewSelector
						initialViewMode={viewMode}
						onViewModeChange={setViewMode}
						enabledViewModes={["preview", "raw"]}
					/>
				</Space>
			}
		>
			<DocumentEditor
				content={content}
				viewMode={viewMode}
				readOnly={readOnly}
				onDocumentChange={readOnly ? undefined : handleChange}
			/>
		</CustomDrawer>
	);
}
